import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import MarkdownEditor from '@/components/ui/MarkdownEditor';
import { faEnvelope, faSave, faTimes } from '@/fontawesome';
import type { CourseMailSequenceEntry } from '@/models';

interface EditMailSequenceEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: CourseMailSequenceEntry | null;
  onSave: (entryId: string, updates: { emailSubject: string; emailBody: string }) => void;
}

export default function EditMailSequenceEntryModal({
  isOpen,
  onClose,
  entry,
  onSave
}: EditMailSequenceEntryModalProps) {
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  useEffect(() => {
    if (entry) {
      setEmailSubject(entry.emailSubject || '');
      setEmailBody(entry.emailBody || '');
    }
  }, [entry]);

  const handleSave = () => {
    if (!entry) return;

    onSave(entry.id, {
      emailSubject,
      emailBody
    });
    onClose();
  };

  const handleCancel = () => {
    // Reset to original values
    if (entry) {
      setEmailSubject(entry.emailSubject || '');
      setEmailBody(entry.emailBody || '');
    }
    onClose();
  };

  if (!entry) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="E-Mail bearbeiten"
      icon={<FontAwesomeIcon icon={faEnvelope} className="w-5 h-5 text-blue-600" />}
      maxWidth="3xl"
    >
      <div className="p-6 space-y-6">
        {/* Entry ID Display */}
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sequenz-Eintrag ID
          </p>
          <p className="font-mono text-gray-900 dark:text-white">
            {entry.id}
          </p>
        </div>

        {/* Email Subject */}
        <div>
          <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            E-Mail Betreff
          </label>
          <input
            id="email-subject"
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Betreff der E-Mail eingeben..."
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Email Body with Markdown Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            E-Mail Inhalt (Markdown)
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <MarkdownEditor
              value={emailBody}
              onChange={(value) => setEmailBody(value || '')}
              placeholder="E-Mail Inhalt eingeben (Markdown wird unterstützt)..."
              height={400}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Sie können Markdown-Formatierung verwenden: **fett**, *kursiv*, [Link](url), etc.
          </p>
        </div>

        {/* Variables Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
            Verfügbare Variablen
          </h4>
          <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <p><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{`{{firstName}}`}</code> - Vorname des Benutzers</p>
            <p><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{`{{lastName}}`}</code> - Nachname des Benutzers</p>
            <p><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{`{{courseTitle}}`}</code> - Titel des Kurses</p>
            <p><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{`{{videoTitle}}`}</code> - Titel des nächsten Videos</p>
          </div>
        </div>
      </div>

      {/* Footer with Actions */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
        >
          <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer"
        >
          <FontAwesomeIcon icon={faSave} className="w-4 h-4 mr-2" />
          Speichern
        </button>
      </div>
    </Modal>
  );
}